import type { FishShopV2ViewerState } from "@cloudnest/fish-shop-v2-engine";
import { initGame } from "@cloudnest/fish-shop-v2-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";
import { resolveFishShopGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApi(): GameApi {
  const nextMode = resolveFishShopGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("fish-shop", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

export interface FishShopMultiplayerState {
  readonly viewerState: FishShopV2ViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
  readonly isObserver: boolean;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

const INITIAL_STATE: FishShopMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
  isObserver: false,
};

let state: FishShopMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }

  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as FishShopV2ViewerState,
      lastAppliedSeq: bestSeq,
    };
  }

  return bestSeq;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: {
      error:
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : fallback,
    },
  };
  notify();
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function waitForCanonicalEvent(roomId: string): Promise<void> {
  for (let attempt = 0; attempt < 8; attempt++) {
    const events = await getGameApi().getGameEvents(roomId);
    if (events.success && (events.data?.length ?? 0) > 0) {
      return;
    }
    await sleep(250);
  }
}

export const fishShopMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    hostPlayerId: string,
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const api = getGameApi();
      const existing = await api.getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatest(existingRows, 0);
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      if (gameApiMode === "game-platform") {
        await api.submitEvent(roomId, "init", {});
      } else {
        const initialState = initGame({
          gameId: roomId,
          playerIds,
          hostPlayerId,
        });
        await api.submitEvent(roomId, "init", { state: initialState });
      }
      await api.submitEvent(roomId, "SyncViewerState", {
        actionId: `${roomId}:sync-initial-view`,
      });

      const projected = await api.getViewerStates(roomId);
      if (projected.success && projected.data) {
        adoptLatest(projected.data, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_FISH_SHOP_INIT_FAILED");
    }
  },

  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const res = await getGameApi().getViewerStates(roomId);
      if (res.success && res.data && state.roomId === roomId) {
        adoptLatest(res.data, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_FISH_SHOP_FETCH_FAILED");
    }
  },

  initializeAsObserver: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      isObserver: true,
      connection: "connecting",
    };
    notify();

    try {
      const api = getGameApi();
      const existing = await api.getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatest(existingRows, 0);
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      await waitForCanonicalEvent(roomId);
      const sync = await api.submitEvent(roomId, "SyncViewerState", {
        actionId: `${roomId}:observer-sync:${myPlayerId}`,
      });
      if (!sync.success) {
        await sleep(300);
        await api.submitEvent(roomId, "SyncViewerState", {
          actionId: `${roomId}:observer-sync-retry:${myPlayerId}`,
        });
      }
      const projected = await api.getViewerStates(roomId);
      if (projected.success && projected.data && state.roomId === roomId) {
        adoptLatest(projected.data, 0);
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_FISH_SHOP_OBSERVER_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const res = await getGameApi().getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (res.success && res.data) {
        adoptLatest(res.data, state.lastAppliedSeq);
        notify();
      }
    } catch (error) {
      setError(error, "ERR_FISH_SHOP_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as FishShopV2ViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  submitAction: async (
    eventType: string,
    payload: Record<string, unknown> = {},
  ): Promise<void> => {
    const roomId = state.roomId;
    if (!roomId) return;
    if (state.isObserver && eventType !== "SyncViewerState") {
      setError(
        "Observers cannot submit game actions",
        "ERR_OBSERVER_READ_ONLY",
      );
      return;
    }

    try {
      const api = getGameApi();
      const res = await api.submitEvent(roomId, eventType, payload);
      if (!res.success) {
        setError(res.error ?? "Action rejected", "ERR_FISH_SHOP_ACTION");
        return;
      }
      const viewerStates = await api.getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (
        viewerStates.success &&
        viewerStates.data &&
        state.roomId === roomId
      ) {
        adoptLatest(viewerStates.data, state.lastAppliedSeq);
      }
      if (state.roomId !== roomId) return;
      state = { ...state, lastError: null };
      notify();
    } catch (error) {
      setError(error, "ERR_FISH_SHOP_ACTION");
    }
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export function useFishShopMultiplayerStore(): FishShopMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

export function getFishShopMultiplayerSnapshot(): FishShopMultiplayerState {
  return state;
}
